import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GetDefaultQuestions } from '../../../redux/questionBankSlice';
import { bulkAddIrlRequests, getIrlFolders } from '../../../apiServices/irlScreenApi';
import { suggestIrl } from '../../NewProjectFlow/projectSetupApi';
import { irlCatalogForWorkstream, irlWhyText } from '../../NewProjectFlow/irlRequestCatalog';
import { PriorityMark } from '../chrome';
import { ListCheckbox, ListNotice } from '../listChrome';
import { buildGeneratedIrlBulkAdd, stockIrlActorFields } from '../listActions';
import { stripHtmlText } from './shared';
import styles from '../irlObservations2026.module.css';

let requestKey = 0;
const nextKey = () => {
    requestKey += 1;
    return `irl-gen-${requestKey}`;
};

function priorityLabel(value) {
    const key = String(value || '').toUpperCase();
    if (key === 'HIGH') return 'High';
    if (key === 'MEDIUM') return 'Medium';
    if (key === 'LOW') return 'Low';
    return 'None';
}

function folderIdFor(folders, name) {
    const match = (folders || []).find(
        folder => String(folder?.name || '').toLowerCase() === String(name || '').toLowerCase(),
    );
    return match?.id ?? null;
}

function toRequestItem(entry, workstreamId, workstreamName, folders) {
    const folder = entry.folder || entry.tag || 'General';
    const request = entry.request || entry.question || '';
    const priorities = entry.priorities || (entry.high || entry.priority === 'High' ? 'HIGH' : 'NONE');
    return {
        key: nextKey(),
        workstreamId,
        folder,
        folderId: entry.folderId || entry.folder_id || folderIdFor(folders, folder),
        request,
        selected: entry.selected !== false && entry.recommended !== false,
        custom: Boolean(entry.custom),
        why: entry.reason || irlWhyText(folder, workstreamName),
        priorities,
        priority: priorityLabel(entry.priority || priorities),
    };
}

function requestsFromSuggestion(requests, workstreamId, workstreamName, folders) {
    return (requests || [])
        .filter(item => String(item.workstream_id) === String(workstreamId))
        .map(item =>
            toRequestItem(
                {
                    folder: item.folder_name || item.folder || 'General',
                    folderId: item.folder_id,
                    request: item.request,
                    priority: item.priority,
                    priorities: item.priorities,
                    selected: item.recommended !== false,
                    reason: item.reason,
                },
                workstreamId,
                workstreamName,
                folders,
            ),
        );
}

function requestsFromCatalog(workstreamName, folders, apiQuestions) {
    const items = irlCatalogForWorkstream(workstreamName).map(entry =>
        toRequestItem(entry, null, workstreamName, folders),
    );
    (apiQuestions || []).forEach(question => {
        const request = (question.question || '').trim();
        if (!request) return;
        if (items.some(item => item.request.toLowerCase() === request.toLowerCase())) return;
        items.push(
            toRequestItem(
                {
                    folder: question.tag || 'General',
                    request,
                    selected: true,
                },
                null,
                workstreamName,
                folders,
            ),
        );
    });
    return items.map(item => ({ ...item, workstreamId: null }));
}

function groupByFolder(items) {
    const groups = [];
    const indexByFolder = new Map();
    items.forEach(item => {
        if (!indexByFolder.has(item.folder)) {
            indexByFolder.set(item.folder, groups.length);
            groups.push({ folder: item.folder, items: [] });
        }
        groups[indexByFolder.get(item.folder)].items.push(item);
    });
    return groups;
}

function actionErrorText(error, fallback) {
    const data = error?.response?.data;
    if (typeof data?.detail === 'string' && data.detail.trim()) return data.detail;
    if (typeof data === 'string' && data.trim() && !data.trim().startsWith('<')) return data;
    if (data && typeof data === 'object') {
        const first = Object.values(data)[0];
        if (Array.isArray(first) && first.length) return String(first[0]);
        if (typeof first === 'string' && first.trim()) return first;
    }
    if (error?.response?.status === 403) return 'Only project administrators can scan the VDR to create an initial IRL.';
    return fallback;
}

function existingKeys(requests) {
    return (requests || [])
        .map(item => {
            const text = stripHtmlText(item.title || item.request || '').trim().toLowerCase();
            if (!text || item.workstreamId == null) return '';
            return `${item.workstreamId}|${text}`;
        })
        .filter(Boolean);
}

export default function IRL_Generate_Initial({ workstreams = [], existingRequests = [], onBack, onCreated }) {
    const dispatch = useDispatch();
    const { selectedProject } = useSelector(state => state.projectData);
    const { user } = useSelector(state => state.user);
    const { projectUsersAll } = useSelector(state => state.team);
    const [requestsByWs, setRequestsByWs] = React.useState({});
    const [foldersByWs, setFoldersByWs] = React.useState({});
    const [activeWsId, setActiveWsId] = React.useState(workstreams[0]?.id || null);
    const [ready, setReady] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [blocked, setBlocked] = React.useState(false);
    const [notice, setNotice] = React.useState('');
    const [noticeTone, setNoticeTone] = React.useState('info');
    const [summary, setSummary] = React.useState(null);
    const [editingKey, setEditingKey] = React.useState(null);
    const [draftRequest, setDraftRequest] = React.useState('');
    const [whyKey, setWhyKey] = React.useState(null);
    const [addingCustom, setAddingCustom] = React.useState(false);
    const [customFolder, setCustomFolder] = React.useState('');
    const [customRequest, setCustomRequest] = React.useState('');
    const workstreamKey = workstreams.map(ws => `${ws.id}:${ws.name}`).join('|');

    React.useEffect(() => {
        if (!workstreams.length) {
            setReady(true);
            setBlocked(true);
            setNoticeTone('error');
            setNotice('Create workstreams before generating an information request list.');
            return undefined;
        }
        let cancelled = false;
        const load = async () => {
            setReady(false);
            setBlocked(false);
            setNotice('');
            const folderResults = await Promise.all(
                workstreams.map(async ws => {
                    try {
                        const data = await getIrlFolders(ws.id);
                        const folders = (data.folders || []).map(folder => ({ id: folder.id, name: folder.name }));
                        return { id: ws.id, name: ws.name, folders };
                    } catch {
                        return { id: ws.id, name: ws.name, folders: [] };
                    }
                }),
            );
            if (cancelled) return;
            setFoldersByWs(Object.fromEntries(folderResults.map(item => [item.id, item.folders])));
            let suggestion = null;
            let scanFailed = false;
            let forbidden = false;
            try {
                const projectId = selectedProject?.id || localStorage.getItem('project_id');
                suggestion = await suggestIrl(
                    projectId,
                    folderResults.map(item => ({ id: item.id, name: item.name, folders: item.folders })),
                );
            } catch (error) {
                scanFailed = true;
                forbidden = error?.response?.status === 403;
                if (!cancelled) {
                    const message = actionErrorText(error, 'The VDR scan could not be completed.');
                    setNoticeTone('error');
                    setNotice(
                        forbidden
                            ? message
                            : `${message} Starting requests from the question catalog are shown instead.`,
                    );
                }
            }
            if (cancelled || forbidden) {
                if (!cancelled && forbidden) {
                    setBlocked(true);
                    setReady(true);
                }
                return;
            }
            const results = await Promise.all(
                workstreams.map(async ws => {
                    const folders = folderResults.find(item => item.id === ws.id)?.folders || [];
                    const suggested = requestsFromSuggestion(suggestion?.requests || [], ws.id, ws.name, folders).map(item => ({
                        ...item,
                        workstreamId: ws.id,
                    }));
                    if (suggested.length) return { id: ws.id, requests: suggested };
                    let apiQuestions = [];
                    try {
                        const questionsRes = await dispatch(GetDefaultQuestions(ws.name));
                        apiQuestions = questionsRes?.payload?.data || [];
                    } catch {
                        apiQuestions = [];
                    }
                    if (cancelled) return { id: ws.id, requests: [] };
                    return {
                        id: ws.id,
                        requests: requestsFromCatalog(ws.name, folders, apiQuestions).map(item => ({
                            ...item,
                            workstreamId: ws.id,
                        })),
                    };
                }),
            );
            if (cancelled) return;
            setRequestsByWs(Object.fromEntries(results.map(item => [item.id, item.requests])));
            setActiveWsId(current => current || workstreams[0]?.id || null);
            setSummary(suggestion?.generation_summary || null);
            if (!scanFailed && suggestion?.source === 'fallback') {
                setNoticeTone('info');
                setNotice('Lens could not tailor requests from the VDR, so these are starting requests for the current folders.');
            }
            setReady(true);
        };
        load();
        return () => {
            cancelled = true;
        };
    // Rescan only when the workstream list or project changes, not on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProject?.id, workstreamKey]);

    const activeWorkstream = workstreams.find(ws => String(ws.id) === String(activeWsId)) || workstreams[0];
    const requests = (activeWorkstream && requestsByWs[activeWorkstream.id]) || [];
    const groups = groupByFolder(requests);
    const folderOptions = React.useMemo(() => {
        const byName = new Map();
        (foldersByWs[activeWorkstream?.id] || []).forEach(folder => {
            if (folder?.name) byName.set(String(folder.name).toLowerCase(), folder);
        });
        requests.forEach(item => {
            if (!item.folder) return;
            const key = String(item.folder).toLowerCase();
            if (!byName.has(key)) byName.set(key, { id: item.folderId, name: item.folder });
        });
        const options = Array.from(byName.values());
        return options.length ? options : [{ id: null, name: 'General' }];
    }, [activeWorkstream?.id, foldersByWs, requests]);

    const selectedRows = workstreams.flatMap(ws =>
        (requestsByWs[ws.id] || []).filter(item => item.selected && item.request.trim()),
    );
    const actorFields = stockIrlActorFields({ user, selectedProject, projectUsersAll });

    const updateRequests = (wsId, updater) => {
        setRequestsByWs(prev => ({
            ...prev,
            [wsId]: updater(prev[wsId] || []),
        }));
    };

    const cancelEdit = () => {
        setEditingKey(null);
        setDraftRequest('');
    };

    const saveEdit = () => {
        if (!activeWorkstream) return;
        const request = draftRequest.trim();
        if (!request) return;
        updateRequests(activeWorkstream.id, list =>
            list.map(item => (item.key === editingKey ? { ...item, request } : item)),
        );
        cancelEdit();
    };

    const addCustom = () => {
        if (!activeWorkstream) return;
        const folder = customFolder.trim();
        const request = customRequest.trim();
        if (!folder || !request) return;
        const folderId = folderOptions.find(item => item.name === folder)?.id || null;
        updateRequests(activeWorkstream.id, list => [
            ...list,
            toRequestItem(
                { folder, folderId, request, selected: true, custom: true, priorities: 'NONE' },
                activeWorkstream.id,
                activeWorkstream.name,
                folderOptions,
            ),
        ]);
        setCustomFolder('');
        setCustomRequest('');
        setAddingCustom(false);
    };

    const handleCreate = async () => {
        if (saving || !ready || editingKey) return;
        if (!selectedProject?.vdr?.id) {
            setNoticeTone('error');
            setNotice('The project data room is not available.');
            return;
        }
        if (!selectedRows.length) {
            setNoticeTone('error');
            setNotice('Select at least one request.');
            return;
        }
        setSaving(true);
        try {
            const { items, skipped } = buildGeneratedIrlBulkAdd(selectedRows, {
                vdrId: selectedProject.vdr.id,
                addedBy: actorFields.addedBy,
                addedByOffline: actorFields.addedByOffline,
                existingKeys: existingKeys(existingRequests),
            });
            const already = skipped.filter(item => item.reason === 'already on the IRL').length;
            if (!items.length) {
                setNoticeTone('error');
                setNotice(
                    already
                        ? 'Those requests are already on the IRL.'
                        : 'Select at least one request.',
                );
                return;
            }
            const saved = await bulkAddIrlRequests({ items });
            const created = Array.isArray(saved) ? saved.length : items.length;
            const noun = created === 1 ? 'request' : 'requests';
            const duplicateNote = already ? ` ${already} already on the IRL were left out.` : '';
            await onCreated?.(`Created ${created} information ${noun}.${duplicateNote}`);
        } catch (error) {
            setNoticeTone('error');
            setNotice(actionErrorText(error, 'The requests could not be created.'));
        } finally {
            setSaving(false);
        }
    };

    const subtitle = !ready
        ? 'Scanning the VDR and project context…'
        : blocked
          ? 'The initial list could not be prepared.'
          : `Lens has prepared an initial information request list from the project context and VDR. Review it before adding the requests.${
                existingRequests.length ? ' Existing requests stay on the list.' : ''
            }`;
    const skippedAnswered = Number(summary?.skipped_as_already_answered || 0);

    return (
        <>
            <div className={styles.contentHeader}>
                <div>
                    <h2 className={styles.contentTitle}>Scan VDR to create initial IRL</h2>
                    <p className={styles.contentSubtitle}>{subtitle}</p>
                </div>
                <div className={styles.headerActions}>
                    <button type="button" className={styles.outlineBtn} onClick={onBack} disabled={saving}>
                        Back
                    </button>
                </div>
            </div>
            <ListNotice message={notice} tone={noticeTone} />
            {ready && !blocked && skippedAnswered > 0 ? (
                <p className={styles.generateNote}>
                    {skippedAnswered} request{skippedAnswered === 1 ? '' : 's'} left out because the VDR already answers them.
                </p>
            ) : null}
            {ready && !blocked ? (
                <>
                    <div className={styles.stockTags}>
                        <p className={styles.stockTagLabel}>Workstreams</p>
                        <div className={styles.stockTagChips}>
                            {workstreams.map(ws => {
                                const list = requestsByWs[ws.id] || [];
                                const active = String(ws.id) === String(activeWorkstream?.id);
                                const selectedCount = list.filter(item => item.selected).length;
                                return (
                                    <button
                                        key={ws.id}
                                        type="button"
                                        className={`${styles.chip} ${active ? styles.chipActive : ''}`}
                                        onClick={() => {
                                            setActiveWsId(ws.id);
                                            cancelEdit();
                                            setAddingCustom(false);
                                            setWhyKey(null);
                                        }}
                                    >
                                        {ws.name} · {selectedCount}/{list.length}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className={styles.stockAddRow}>
                        <div className={styles.stockAddLeft}>
                            <button
                                type="button"
                                className={styles.ghostBtn}
                                disabled={!requests.length || saving}
                                onClick={() =>
                                    updateRequests(activeWorkstream.id, list => list.map(item => ({ ...item, selected: true })))
                                }
                            >
                                Select all
                            </button>
                            <button
                                type="button"
                                className={styles.ghostBtn}
                                disabled={!requests.length || saving}
                                onClick={() =>
                                    updateRequests(activeWorkstream.id, list => list.map(item => ({ ...item, selected: false })))
                                }
                            >
                                Deselect all
                            </button>
                            <span className={styles.stockSelectedCount}>
                                {selectedRows.length} selected
                            </span>
                        </div>
                        <button
                            type="button"
                            className={styles.primaryBtn}
                            onClick={handleCreate}
                            disabled={saving || Boolean(editingKey) || !selectedRows.length}
                        >
                            {saving ? 'Creating…' : 'Create initial IRL'}
                        </button>
                    </div>
                    {requests.length === 0 && !addingCustom ? (
                        <p className={styles.generateNote}>No recommended requests for this workstream. Add one to get started.</p>
                    ) : null}
                    {groups.map(group => (
                        <section key={group.folder}>
                            <h3 className={styles.generateGroup}>{group.folder}</h3>
                            <div className={`${styles.tableWrap} ${styles.generateTable}`}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th className={styles.checkCol}>
                                                <span className={styles.visuallyHidden}>Select</span>
                                            </th>
                                            <th>Request</th>
                                            <th>Priority</th>
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {group.items.map(item => {
                                            const editing = editingKey === item.key;
                                            return (
                                                <tr key={item.key}>
                                                    <td className={styles.checkCol}>
                                                        <ListCheckbox
                                                            checked={item.selected}
                                                            onChange={() =>
                                                                updateRequests(activeWorkstream.id, list =>
                                                                    list.map(entry =>
                                                                        entry.key === item.key
                                                                            ? { ...entry, selected: !entry.selected }
                                                                            : entry,
                                                                    ),
                                                                )
                                                            }
                                                            label={`Select ${item.request}`}
                                                        />
                                                    </td>
                                                    <td>
                                                        {editing ? (
                                                            <div className={styles.generateEdit}>
                                                                <textarea
                                                                    className={styles.textarea}
                                                                    value={draftRequest}
                                                                    onChange={event => setDraftRequest(event.target.value)}
                                                                    rows={2}
                                                                />
                                                                <div className={styles.generateActions}>
                                                                    <button
                                                                        type="button"
                                                                        className={styles.primaryBtn}
                                                                        disabled={!draftRequest.trim()}
                                                                        onClick={saveEdit}
                                                                    >
                                                                        Save
                                                                    </button>
                                                                    <button type="button" className={styles.ghostBtn} onClick={cancelEdit}>
                                                                        Cancel
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <p className={styles.generateRequest}>{item.request}</p>
                                                                {whyKey === item.key ? <p className={styles.generateWhy}>{item.why}</p> : null}
                                                            </>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {item.priority === 'None' ? (
                                                            <span className={styles.metaPlain}>None</span>
                                                        ) : (
                                                            <PriorityMark value={item.priority} />
                                                        )}
                                                    </td>
                                                    <td>
                                                        {editing ? null : (
                                                            <div className={styles.generateActions}>
                                                                <button
                                                                    type="button"
                                                                    className={styles.textBtn}
                                                                    onClick={() => {
                                                                        setAddingCustom(false);
                                                                        setWhyKey(null);
                                                                        setEditingKey(item.key);
                                                                        setDraftRequest(item.request);
                                                                    }}
                                                                >
                                                                    Edit
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={styles.textBtn}
                                                                    onClick={() => setWhyKey(current => (current === item.key ? null : item.key))}
                                                                >
                                                                    Why?
                                                                </button>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </section>
                    ))}
                    {addingCustom ? (
                        <div className={styles.generateCustom}>
                            <select
                                className={styles.selectField}
                                value={customFolder}
                                onChange={event => setCustomFolder(event.target.value)}
                                aria-label="Folder"
                            >
                                <option value="">Select folder</option>
                                {folderOptions.map(folder => (
                                    <option key={folder.name} value={folder.name}>
                                        {folder.name}
                                    </option>
                                ))}
                            </select>
                            <textarea
                                className={styles.textarea}
                                value={customRequest}
                                onChange={event => setCustomRequest(event.target.value)}
                                placeholder="Information request text"
                                rows={2}
                            />
                            <div className={styles.generateActions}>
                                <button
                                    type="button"
                                    className={styles.primaryBtn}
                                    disabled={!customFolder.trim() || !customRequest.trim()}
                                    onClick={addCustom}
                                >
                                    Add request
                                </button>
                                <button
                                    type="button"
                                    className={styles.ghostBtn}
                                    onClick={() => {
                                        setAddingCustom(false);
                                        setCustomFolder('');
                                        setCustomRequest('');
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className={styles.generateAdd}
                            onClick={() => {
                                cancelEdit();
                                setWhyKey(null);
                                setAddingCustom(true);
                            }}
                        >
                            + Add information request
                        </button>
                    )}
                </>
            ) : null}
        </>
    );
}
